/**
 * Chunk C1–C2 — Bridge app Past-orders check-in → dashboard GR local suggestions.
 *
 * Source of truth (after B2):
 * - App Delivered → Order.isDeleivered (boolean)
 * - App Report → Receipting row: error + notes + qty, status Not Saved
 *   (legacy Title Case "Sent to Supplier" still treated as open check-in)
 *
 * C2: applied as editable defaults on expand + clear “From app check-in” copy.
 * Suggestions only — never auto-Save. Manager must Confirm via Save / Accept.
 *
 * Chunk B5: voided POs never get As ordered / Report suggestions.
 */

import { getReceiptingLineId } from './goodsReceiptingAsOrderedHelpers'
import { canEditGrLineCheckIn } from './goodsReceiptingActionHelpers'
import {
  getDefaultAskForCredit,
  getReceiptingLineStatusForCredit,
} from './goodsReceiptingCreditHelpers'
import { isVoidedGoodsReceiptingOrder } from './goodsReceiptingVoidHelpers'

export const APP_CHECK_IN_REPORT_ERRORS = [
  'Damaged',
  'Not Delivered',
  'Quality Issue',
]

export const GR_APP_CHECK_IN_COPY = {
  confirmBanner:
    'Checked on the app — confirm with Save receipt. Short / damaged lines default Ask for credit? on (untick if you don’t need a supplier credit). Use Accept only when everything arrived as ordered.',
  fromAppChip: 'From app check-in',
}

/** Open app Report status values (not dashboard credit workflow / Full accept). */
export function isAppReportCheckInStatus(status) {
  const raw = String(status || '').trim()
  if (!raw || raw === 'Not Saved') return true
  // Legacy app Report casing before B2
  if (raw === 'Sent to Supplier') return true
  return false
}

export function hasAppReportCheckIn(line = {}) {
  const receipting = line?.receipting || {}
  const error = String(receipting.error || '').trim()
  if (!APP_CHECK_IN_REPORT_ERRORS.includes(error)) return false
  return isAppReportCheckInStatus(receipting.status ?? line?.status)
}

export function hasAppDeliveredCheckIn(line = {}) {
  return line?.isDeleivered === true
}

/**
 * Build a draft Report Error row from app check-in receipting fields.
 * Short reasons default Ask for credit? on (manager can untick before Save).
 */
export function buildSuggestedReportErrorRow(line = {}) {
  const receipting = line?.receipting || {}
  const error = String(receipting.error || '').trim()
  const askForCredit = getDefaultAskForCredit(error)
  const row = {
    order: line._id,
    error,
    notes: receipting.notes ?? '',
    askForCredit,
    supplier: line.supplier,
    purchaseOrderNumber: line.purchaseOrderNumber,
    status: getReceiptingLineStatusForCredit(askForCredit),
    fromAppCheckIn: true,
  }
  if (receipting.updatedQuantity != null) {
    row.updatedQuantity = receipting.updatedQuantity
  }
  if (receipting.previousQty != null) {
    row.previousQty = receipting.previousQty
  }
  if (receipting.orderedQuantity != null) {
    row.orderedQuantity = receipting.orderedQuantity
  }
  return row
}

/**
 * Per-line suggestion from app signals. Report wins over Delivered.
 * @returns {'as_ordered'|'report_error'|null}
 */
export function resolveAppCheckInSuggestion(line = {}, parentOrder = {}) {
  if (
    isVoidedGoodsReceiptingOrder(parentOrder) ||
    isVoidedGoodsReceiptingOrder(line)
  ) {
    return null
  }
  if (!canEditGrLineCheckIn(line, parentOrder)) return null
  if (hasAppReportCheckIn(line)) return 'report_error'
  if (hasAppDeliveredCheckIn(line)) return 'as_ordered'
  return null
}

/**
 * Soft chip when local draft still reflects an app check-in default.
 */
export function shouldShowAppCheckInAffordance(
  line = {},
  { locallyAsOrdered = false, hasLocalReportError = false } = {},
  parentOrder = {},
) {
  if (!canEditGrLineCheckIn(line, parentOrder)) return false
  if (hasLocalReportError && hasAppReportCheckIn(line)) return true
  if (
    locallyAsOrdered &&
    hasAppDeliveredCheckIn(line) &&
    !hasAppReportCheckIn(line)
  ) {
    return true
  }
  return false
}

/**
 * Banner above expanded lines when app check-in defaults are available.
 * Hidden when PO is already receipted / not editable.
 */
export function shouldShowAppCheckInConfirmBanner(
  lineOrders = [],
  parentOrder = {},
) {
  if (isVoidedGoodsReceiptingOrder(parentOrder)) return false
  if (!canEditGrLineCheckIn({}, parentOrder)) return false
  return (lineOrders || []).some(
    line => resolveAppCheckInSuggestion(line, parentOrder) != null,
  )
}

/**
 * Collect suggestions for a PO's loaded lines (no merge yet).
 */
export function buildAppCheckInSuggestions(lineOrders = [], parentOrder = {}) {
  if (isVoidedGoodsReceiptingOrder(parentOrder)) {
    return { asOrderedLineIds: [], reportErrorRows: [] }
  }
  const asOrderedLineIds = []
  const reportErrorRows = []

  for (const line of lineOrders || []) {
    const kind = resolveAppCheckInSuggestion(line, parentOrder)
    const id = getReceiptingLineId(line)
    if (!id || !kind) continue
    if (kind === 'report_error') {
      reportErrorRows.push(buildSuggestedReportErrorRow(line))
    } else if (kind === 'as_ordered') {
      asOrderedLineIds.push(id)
    }
  }

  return { asOrderedLineIds, reportErrorRows }
}

/**
 * Merge suggestions into existing local draft without overwriting manager edits.
 * Only fills lines that are not already in asOrderedLineIds or editedReceipting.
 */
export function mergeAppCheckInSuggestions({
  asOrderedLineIds = [],
  editedReceipting = [],
  suggestions = { asOrderedLineIds: [], reportErrorRows: [] },
} = {}) {
  const occupied = new Set([
    ...(asOrderedLineIds || []).map(String),
    ...(editedReceipting || [])
      .map(row => getReceiptingLineId(row.order))
      .filter(Boolean),
  ])

  const nextAsOrdered = [...(asOrderedLineIds || [])]
  for (const id of suggestions.asOrderedLineIds || []) {
    const key = String(id)
    if (!key || occupied.has(key)) continue
    nextAsOrdered.push(key)
    occupied.add(key)
  }

  const nextEdited = [...(editedReceipting || [])]
  for (const row of suggestions.reportErrorRows || []) {
    const key = getReceiptingLineId(row.order)
    if (!key || occupied.has(key)) continue
    nextEdited.push(row)
    occupied.add(key)
  }

  return {
    asOrderedLineIds: nextAsOrdered,
    editedReceipting: nextEdited,
  }
}
